import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SimpleModal } from '@/components/SimpleModal';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Users, 
  Plus, 
  Edit2, 
  Trash2, 
  User, 
  Mail, 
  Phone, 
  CreditCard,
  Percent,
  Shield,
  ShoppingCart,
  UserCheck,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react';

const API_URL = BACKEND_URL;

export const UsersPage = () => {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'caixa',
    cpf: '',
    phone: '',
    comissao: 0
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tenant/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.users) {
        setUsers(data.users);
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao carregar usuários', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const url = editingUser 
        ? `${API_URL}/api/tenant/users/${editingUser.id}`
        : `${API_URL}/api/tenant/users`;
      
      const method = editingUser ? 'PUT' : 'POST';
      
      // Para edição, só enviar senha se foi preenchida
      const payload = { ...formData };
      if (editingUser && !payload.password) {
        delete payload.password;
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({ title: 'Sucesso', description: data.message });
        setShowModal(false);
        resetForm();
        fetchUsers();
      } else {
        toast({ title: 'Erro', description: data.detail || 'Erro ao salvar', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Tem certeza que deseja EXCLUIR o usuário ${user.name}?\n\nEsta ação não pode ser desfeita!`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tenant/users/${user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({ title: 'Sucesso', description: 'Usuário excluído' });
        fetchUsers();
      } else {
        toast({ title: 'Erro', description: data.detail || 'Erro ao excluir', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.active === false ? true : false;
    const action = newStatus ? 'ativar' : 'desativar';
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tenant/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active: newStatus })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({ title: 'Sucesso', description: `Usuário ${newStatus ? 'ativado' : 'desativado'}` });
        fetchUsers();
      } else {
        toast({ title: 'Erro', description: data.detail || `Erro ao ${action}`, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      cpf: user.cpf || '',
      phone: user.phone || '',
      comissao: user.comissao || 0
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'caixa',
      cpf: '',
      phone: '',
      comissao: 0
    });
  };

  const openNewUser = () => {
    resetForm();
    setShowModal(true);
  };

  const getRoleBadge = (role) => {
    const roles = {
      admin: { label: 'Admin', color: 'bg-purple-500' },
      gerente: { label: 'Gerente', color: 'bg-blue-500' },
      caixa: { label: 'Caixa', color: 'bg-emerald-500' }
    };
    const config = roles[role] || { label: role, color: 'bg-slate-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4" />;
      case 'gerente': return <UserCheck className="w-4 h-4" />;
      case 'caixa': return <ShoppingCart className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Users className="w-8 h-8 text-emerald-600" />
            Usuários
          </h1>
          <p className="text-slate-500 mt-1">Gerencie os usuários da sua empresa</p>
        </div>
        
        <div className="flex gap-3">
          <Button onClick={fetchUsers} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button onClick={openNewUser} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-user-button">
            <Plus className="w-4 h-4 mr-2" />
            Novo Usuário
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
              <Users className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Admins</p>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin').length}</p>
              </div>
              <Shield className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Gerentes</p>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'gerente').length}</p>
              </div>
              <UserCheck className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Caixas</p>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'caixa').length}</p>
              </div>
              <ShoppingCart className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Usuários</CardTitle>
          <CardDescription>Usuários cadastrados no sistema</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum usuário cadastrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Nome</th>
                    <th className="text-left py-3 px-4">Email</th>
                    <th className="text-left py-3 px-4">Tipo</th>
                    <th className="text-left py-3 px-4">CPF</th>
                    <th className="text-left py-3 px-4">Telefone</th>
                    <th className="text-left py-3 px-4">Comissão</th>
                    <th className="text-center py-3 px-4">Status</th>
                    <th className="text-right py-3 px-4">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getRoleIcon(user.role)}
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{user.email}</td>
                      <td className="py-3 px-4">{getRoleBadge(user.role)}</td>
                      <td className="py-3 px-4 text-slate-600">{user.cpf || '-'}</td>
                      <td className="py-3 px-4 text-slate-600">{user.phone || '-'}</td>
                      <td className="py-3 px-4 text-slate-600">{user.comissao ? `${user.comissao}%` : '-'}</td>
                      <td className="py-3 px-4 text-center">
                        {currentUser?.id !== user.id ? (
                          <div className="flex items-center justify-center gap-2">
                            <Switch
                              checked={user.active !== false}
                              onCheckedChange={() => handleToggleStatus(user)}
                              data-testid={`toggle-user-${user.id}`}
                            />
                            <span className={`text-xs ${user.active !== false ? 'text-emerald-600' : 'text-slate-400'}`}>
                              {user.active !== false ? 'Ativo' : 'Inativo'}
                            </span>
                          </div>
                        ) : (
                          <Badge className="bg-emerald-500">Ativo</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(user)} data-testid={`edit-user-${user.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {/* Não mostrar botão de excluir para o próprio usuário */}
                          {currentUser?.id !== user.id && (
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(user)} data-testid={`delete-user-${user.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar/Editar */}
      <SimpleModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingUser ? 'Editar Usuário' : 'Novo Usuário'}
      >
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="name">Nome Completo *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome do usuário"
                required
                data-testid="input-name"
              />
            </div>
            
            <div className="col-span-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@exemplo.com"
                required
                disabled={!!editingUser}
                data-testid="input-email"
              />
            </div>
            
            <div className="col-span-2">
              <Label htmlFor="password">{editingUser ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="********"
                  required={!editingUser}
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="role">Tipo de Usuário *</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger data-testid="select-role">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="gerente">Gerente</SelectItem>
                  <SelectItem value="caixa">Caixa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="comissao">Comissão (%)</Label>
              <Input
                id="comissao"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.comissao}
                onChange={(e) => setFormData({ ...formData, comissao: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                data-testid="input-comissao"
              />
            </div>
            
            <div>
              <Label htmlFor="cpf">CPF</Label>
              <Input
                id="cpf"
                value={formData.cpf}
                onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                placeholder="000.000.000-00"
                data-testid="input-cpf"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Telefone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(00) 00000-0000"
                data-testid="input-phone"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-user-button">
              {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
            </Button>
          </div>
        </form>
      </SimpleModal>
    </div>
  );
};
